/* ValidAlpha moving background: a field of price-like lines, one signal line and a ghost order book.
   startField(canvas) starts it and returns a cleanup function. */

/* ---------- Simplex noise 3D (Gustavson), seeded ---------- */
const G = [[1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],[1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],[0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1]];
const perm = new Uint8Array(512), pm12 = new Uint8Array(512);
let seed = 20200610;
const rnd = () => (seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296;
const p = Array.from({length:256}, (_, i) => i);
for (let i = 255; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [p[i], p[j]] = [p[j], p[i]]; }
for (let i = 0; i < 512; i++) { perm[i] = p[i & 255]; pm12[i] = perm[i] % 12; }
const F3 = 1/3, G3 = 1/6;
function noise(x, y, z) {
  const s = (x + y + z) * F3, i = Math.floor(x + s), j = Math.floor(y + s), k = Math.floor(z + s);
  const t = (i + j + k) * G3, x0 = x - i + t, y0 = y - j + t, z0 = z - k + t;
  let i1,j1,k1,i2,j2,k2;
  if (x0 >= y0) {
    if (y0 >= z0) { i1=1;j1=0;k1=0;i2=1;j2=1;k2=0; } else if (x0 >= z0) { i1=1;j1=0;k1=0;i2=1;j2=0;k2=1; } else { i1=0;j1=0;k1=1;i2=1;j2=0;k2=1; }
  } else {
    if (y0 < z0) { i1=0;j1=0;k1=1;i2=0;j2=1;k2=1; } else if (x0 < z0) { i1=0;j1=1;k1=0;i2=0;j2=1;k2=1; } else { i1=0;j1=1;k1=0;i2=1;j2=1;k2=0; }
  }
  const x1=x0-i1+G3, y1=y0-j1+G3, z1=z0-k1+G3, x2=x0-i2+2*G3, y2=y0-j2+2*G3, z2=z0-k2+2*G3, x3=x0-1+3*G3, y3=y0-1+3*G3, z3=z0-1+3*G3;
  const ii=i&255, jj=j&255, kk=k&255;
  const c = (gi, a, b, d) => { let q = .6 - a*a - b*b - d*d; if (q < 0) return 0; q *= q; const g = G[gi]; return q*q*(g[0]*a + g[1]*b + g[2]*d); };
  return 32 * (c(pm12[ii+perm[jj+perm[kk]]], x0,y0,z0) + c(pm12[ii+i1+perm[jj+j1+perm[kk+k1]]], x1,y1,z1)
             + c(pm12[ii+i2+perm[jj+j2+perm[kk+k2]]], x2,y2,z2) + c(pm12[ii+1+perm[jj+1+perm[kk+1]]], x3,y3,z3));
}

/* ---------- Field ---------- */
// Animates the background field on `cv`; returns a cleanup that stops the loop and removes listeners.
export function startField(cv) {
  const ctx = cv.getContext('2d');
  if (!ctx) return undefined;                    // no canvas support: leave the static page
  const LM = 8, lm = document.createElement('canvas'), lx = lm.getContext('2d');
  const SAGE = getComputedStyle(document.documentElement).getPropertyValue('--sage-rgb').trim() || '120,169,169';
  const MIST = '211,223,221';                     // asks take the cooler, paler tint; bids the brand sage
  let maxDpr = 1.5, W = 0, H = 0, dpr = 1, rows = [], step = 7, tick = 8, levels = 16;
  const lens = { x: 0, y: 0, tx: 0, ty: 0, r: 260 };
  let lastPointer = -1e9;

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
    W = window.innerWidth; H = window.innerHeight;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    lm.width = Math.ceil(W / LM); lm.height = Math.ceil(H / LM); lx.setTransform(1 / LM, 0, 0, 1 / LM, 0, 0);
    const n = Math.max(36, Math.min(96, Math.round(H / 10)));
    const gap = (H + 80) / n;
    rows = Array.from({length: n}, (_, i) => -40 + gap * (i + .5));
    step = Math.max(6, Math.round(W / 240));       // ~240 samples per line at any width
    tick = W < 640 ? 6 : Math.max(7, Math.min(10, H / 90));  // one price tick, in px
    levels = W < 640 ? 10 : 16;                    // depth shown per side
    lens.r = Math.max(200, Math.min(640, Math.max(W, H) * .26));
    if (!lens.x) { lens.x = lens.tx = W * .66; lens.y = lens.ty = H * .52; }
  }

  const onPointerMove = e => { lens.tx = e.clientX; lens.ty = e.clientY; lastPointer = performance.now(); };

  // The signal line: the one path that resolves out of the field. It stops at "now"; nothing is drawn past it.
  const headFrac = .84;

  /* ---------- Book: a ghost depth ladder hanging off the head of the signal ---------- */
  // Right of "now" is the resting liquidity. Levels are keyed by absolute price row (y / tick), so size
  // stays put while the mid moves. It updates in discrete steps, much faster than the field drifts.
  const book = new Map(), prints = [];
  const BOOK_HZ = 24;
  let bookClock = 0, touch = null, bookY = null;
  const freshSize = d => (.25 + .75 * (1 - Math.exp(-d / 4))) * (.6 + .8 * Math.random()) * (Math.random() < .08 ? 1.8 : 1);
  const sizeAt = (l, d) => { let s = book.get(l); if (s === undefined) { s = freshSize(d); book.set(l, s); } return s; };

  // a = best-ask row; bids start at a + 1. One step is a handful of adds and cancels, weighted to the touch.
  function stepBook(a, t) {
    for (let e = 2 + Math.floor(Math.random() * 5); e > 0; e--) {
      const d = Math.min(levels - 1, Math.floor(-Math.log(1 - Math.random()) * 3));
      const l = Math.random() < .5 ? a - d : a + 1 + d, s = sizeAt(l, d);
      book.set(l, Math.min(2.2, Math.max(.06, Math.random() < .55 ? s + .08 + .25 * Math.random() : s * (.55 + .35 * Math.random()))));
    }
    if (Math.random() < .07) {                     // a print at the touch eats into the queue
      const l = Math.random() < .5 ? a : a + 1;
      book.set(l, Math.max(.06, sizeAt(l, 0) - .3 - .4 * Math.random()));
      prints.push({ l, t });
    }
  }

  // The mid ticked: levels that changed side were traded through and re-queue small on the new side.
  function moveTouch(a, t) {
    if (a === touch) return;
    if (touch !== null) {
      const lo = Math.min(a, touch) + 1, hi = Math.max(a, touch);
      for (let l = lo; l <= hi && l - lo < 3; l++) { book.set(l, .15 + .3 * Math.random()); prints.push({ l, t }); }
    }
    touch = a;
    for (const l of book.keys()) if (Math.abs(l - a) > 64) book.delete(l);
  }

  function drawBook(head, hy, t, ease) {
    const a = Math.floor(hy / tick), bh = Math.max(2, tick * .4);
    bookY = hy;
    moveTouch(a, t);
    if (t - bookClock > 1) bookClock = t;          // back from a hidden tab: don't replay the gap
    while (t - bookClock >= 1 / BOOK_HZ) { stepBook(a, t); bookClock += 1 / BOOK_HZ; }
    const x0 = head + 12, maxLen = Math.max(0, W - x0 - (W < 640 ? 8 : W * .035));
    for (let i = 0; i < levels; i++) {
      const alpha = (i === 0 ? .4 : .05 + .22 * Math.exp(-i / (levels * .45))) * ease;
      for (const [l, rgb] of [[a - i, MIST], [a + 1 + i, SAGE]]) {
        ctx.fillStyle = `rgba(${rgb},${alpha})`;
        ctx.fillRect(x0, Math.round(l * tick - bh / 2), Math.min(1, sizeAt(l, i) / 2.2) * maxLen, bh);
      }
    }
    for (let k = prints.length - 1; k >= 0; k--) {
      const age = (t - prints[k].t) / .4;
      if (age >= 1 || age < 0) { prints.splice(k, 1); continue; }
      ctx.fillStyle = `rgba(${MIST},${.55 * (1 - age) * ease})`;
      ctx.fillRect(x0, Math.round(prints[k].l * tick - bh / 2), maxLen * .3, bh);
    }
  }

  // Own quotes: best ask and best bid either side of the signal, stepped to the price grid, fading in toward now.
  function drawQuotes(pts, head, ease) {
    const qx = head - W * (W < 640 ? .3 : .22);
    for (const [side, rgb] of [[0, MIST], [1, SAGE]]) {
      const g = ctx.createLinearGradient(qx, 0, head, 0);
      g.addColorStop(0, `rgba(${rgb},0)`); g.addColorStop(1, `rgba(${rgb},${.3 * ease})`);
      ctx.strokeStyle = g; ctx.lineWidth = 1; ctx.beginPath();
      let py = null;
      for (let j = 0; j < pts.length; j += 2) {
        if (pts[j] < qx) continue;
        const x = pts[j], y = (Math.floor(pts[j + 1] / tick) + side) * tick;
        if (py === null) ctx.moveTo(x, y); else if (y !== py) { ctx.lineTo(x, py); ctx.lineTo(x, y); }
        py = y;
      }
      if (py !== null) { ctx.lineTo(head + 12, py); ctx.stroke(); }   // runs into the touch level
    }
  }

  function draw(now) {
    const t = now / 1000;
    const intro = Math.min(1, t / 2.4);
    const ease = 1 - Math.pow(1 - intro, 3);

    if (now - lastPointer > 3500) {             // drift when idle or on touch
      lens.tx = W * (.62 + .2 * Math.sin(t * .11)); lens.ty = H * (.5 + .22 * Math.sin(t * .07 + 1.3));
    }
    lens.x += (lens.tx - lens.x) * .045; lens.y += (lens.ty - lens.y) * .045;

    ctx.clearRect(0, 0, W, H);
    const z = t * .12, drift = t * .06, R2 = 2 * lens.r * lens.r;
    const field = new Path2D();
    for (let r = 0; r < rows.length; r++) {
      const y0 = rows[r], ny = y0 * .0042;
      for (let x = -step; x <= W + step; x += step) {
        const nx = x * .0017 + drift;            // field flows right-to-left, like time
        const n = noise(nx, ny, z) + .45 * noise(nx * 2.3, ny * 2.3, z * 1.6 + 7);
        const dx = x - lens.x, dy = y0 - lens.y;
        const k = Math.exp(-(dx*dx + dy*dy) / R2);
        const y = y0 + n * (18 + 38 * k) * ease;
        if (x < 0) field.moveTo(x, y); else field.lineTo(x, y);
      }
    }
    // Brightness map at 1/8 resolution: base level + lens focus + a slow band of light that
    // sweeps right-to-left every ~18 s. Lines are stroked once, flat, then masked by this map.
    lx.globalCompositeOperation = 'copy';
    lx.fillStyle = `rgba(0,0,0,${.14 * ease})`; lx.fillRect(0, 0, W, H);
    lx.globalCompositeOperation = 'lighter';
    const lg0 = lx.createRadialGradient(lens.x, lens.y, 0, lens.x, lens.y, lens.r);
    lg0.addColorStop(0, `rgba(0,0,0,${.5 * ease})`); lg0.addColorStop(.5, `rgba(0,0,0,${.14 * ease})`); lg0.addColorStop(1, 'rgba(0,0,0,0)');
    lx.fillStyle = lg0; lx.fillRect(0, 0, W, H);
    const span = W * .4, period = 18, ph = ((t + 4) % period) / period;
    const bx = W + span - ph * (W + 2 * span);
    const sw = lx.createLinearGradient(bx - span, 0, bx + span, 0);
    sw.addColorStop(0, 'rgba(0,0,0,0)'); sw.addColorStop(.5, `rgba(0,0,0,${.2 * ease})`); sw.addColorStop(1, 'rgba(0,0,0,0)');
    lx.fillStyle = sw; lx.fillRect(0, 0, W, H);
    if (bookY !== null) {                         // quiet the field behind the book so the ladder reads cleanly
      const R = levels * tick * 1.4, cg = lx.createLinearGradient(0, bookY - R, 0, bookY + R);
      cg.addColorStop(0, 'rgba(0,0,0,0)'); cg.addColorStop(.3, 'rgba(0,0,0,.65)'); cg.addColorStop(.7, 'rgba(0,0,0,.65)'); cg.addColorStop(1, 'rgba(0,0,0,0)');
      lx.globalCompositeOperation = 'destination-out';
      lx.fillStyle = cg; lx.fillRect(W * headFrac + 4, bookY - R, W, 2 * R);
    }

    ctx.lineWidth = 1; ctx.strokeStyle = `rgb(${SAGE})`; ctx.stroke(field);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(lm, 0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';

    // signal
    const sy0 = H * (W < 640 ? .68 : .6), head = W * headFrac;
    const rise = H * (W < 640 ? .14 : .22) * ease;  // net climb from left edge to the head; lifts in on load
    const lg = ctx.createLinearGradient(0, 0, head, 0);
    lg.addColorStop(0, `rgba(${SAGE},0)`); lg.addColorStop(.35, `rgba(${SAGE},${.2 * ease})`); lg.addColorStop(1, `rgba(${SAGE},${.9 * ease})`);
    ctx.strokeStyle = lg; ctx.lineWidth = 1.5;
    ctx.beginPath();
    let hy = sy0;
    const pts = [];
    for (let x = -step; x <= head; x += step / 2) {
      const u = (x - head) * .0026;                // anchored to the head so history scrolls left
      const trend = rise * ((head - x) / head - .5);  // lower on the left, higher at the head
      const y = sy0 + trend + (noise(u + t * .16, 3.3, 0) * 20 + noise(u * 3 + t * .45, 9.1, 0) * 6) * ease;
      if (x < 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      hy = y; pts.push(x, y);
    }
    ctx.stroke();
    drawQuotes(pts, head, ease);
    drawBook(head, hy, t, ease);
    ctx.fillStyle = `rgba(${SAGE},${.9 * ease})`;
    ctx.beginPath(); ctx.arc(head, hy, 2.2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = `rgba(${SAGE},${.12 * ease})`;
    ctx.beginPath(); ctx.arc(head, hy, 7 + 2 * Math.sin(t * 2.6), 0, Math.PI * 2); ctx.fill();
  }

  // Touch devices (mostly phones on battery): 30 fps looks the same here at about half the cost.
  const FRAME = window.matchMedia('(pointer: coarse)').matches ? 1000 / 30 : 0;
  // Adaptive quality: if frames run slow (large or high-DPI screens without GPU help), drop canvas resolution.
  let raf = 0, last = 0, ema = Math.max(16, FRAME), frames = 0;
  const loop = now => {
    raf = requestAnimationFrame(loop);
    if (last && now - last < FRAME - 2) return;
    if (last) { ema += ((now - last) - ema) * .05; frames++; }
    last = now;
    if (frames > 20 && ema > Math.max(24, FRAME * 1.45) && maxDpr > 1) { maxDpr = Math.max(1, maxDpr - .5); resize(); frames = 0; ema = Math.max(16, FRAME); }
    draw(now);
  };
  const onVisibility = () => {
    cancelAnimationFrame(raf); last = 0; if (!document.hidden) raf = requestAnimationFrame(loop);
  };

  resize();
  window.addEventListener('pointermove', onPointerMove, { passive: true });
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', onVisibility);
  raf = requestAnimationFrame(loop);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
